<div class="w-full">
  <!-- Subtitle + Period Selector -->
  <div class="flex flex-wrap items-center justify-between gap-4 mb-4">
    <div class="flex flex-col">
      <h2 class="text-xl font-semibold text-text-primary m-0">Auswertung der Lieferzettel</h2>
      <p class="text-sm text-text-muted">Auswertung aller Positionen im Berichtszeitraum</p>
    </div>

    <div class="flex items-center gap-3">
      <div class="flex items-center bg-white/8 rounded-xl p-1">
        @for (period of periodModes; track period.key) {
          <button
            type="button"
            class="px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            [class]="mode() === period.key ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary hover:bg-white/5'"
            (click)="setMode(period.key)"
          >
            {{ period.label }}
          </button>
        }
      </div>

      @if (mode() === 'custom') {
        <p-datepicker
          class="w-72"
          selectionMode="range"
          [ngModel]="customRange()"
          (ngModelChange)="customRange.set($event)"
          dateFormat="dd.mm.yy"
          placeholder="Zeitraum wählen"
          [readonlyInput]="true"
          [showIcon]="true"
          appendTo="body"
          styleClass="datepicker-component-style"
          panelStyleClass="datepicker-panel-style"
        ></p-datepicker>
      } @else {
        <div class="flex items-center gap-1 bg-white/8 rounded-xl px-2 py-1.5">
          <button
            type="button"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors cursor-pointer"
            (click)="shift(-1)"
            aria-label="Vorheriger Zeitraum"
          >
            <span class="material-icons text-xl">chevron_left</span>
          </button>
          @if (mode() === 'day') {
            <p-datepicker
              class="w-40"
              [ngModel]="dayValue()"
              (ngModelChange)="onDayPicked($event)"
              dateFormat="dd.mm.yy"
              [readonlyInput]="true"
              [showIcon]="true"
              appendTo="body"
              styleClass="datepicker-component-style"
              panelStyleClass="datepicker-panel-style"
            ></p-datepicker>
          } @else {
            <span class="text-sm font-medium text-text-primary px-2 whitespace-nowrap">{{ rangeLabel() }}</span>
          }
          <button
            type="button"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors cursor-pointer"
            (click)="shift(1)"
            aria-label="Nächster Zeitraum"
          >
            <span class="material-icons text-xl">chevron_right</span>
          </button>
        </div>
      }
    </div>
  </div>

  <!-- Error State -->
  @if (errorMessage()) {
    <div class="glass-card p-6 rounded-2xl mb-4 flex items-center justify-between gap-4">
      <p class="text-sm text-text-secondary m-0">{{ errorMessage() }}</p>
      <button type="button" class="btn-secondary" (click)="reload()">Erneut versuchen</button>
    </div>
  }

  <!-- KPI Cards -->
  <div class="grid grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
    @if (isLoading()) {
      @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <div class="glass-card flex items-center p-4 rounded-2xl animate-pulse">
          <div class="rounded-[14px] bg-white/10 mr-4"></div>
          <div class="flex-1 space-y-2">
            <div class="h-7 bg-white/10 rounded w-16"></div>
            <div class="h-4 bg-white/8 rounded w-28"></div>
          </div>
        </div>
      }
    } @else {
      @for (stat of kpis(); track stat.title) {
        <div class="glass-card flex items-start p-3 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div class="stat-icon h-10 rounded-[14px] flex items-center justify-center mr-4 text-white">
            <span class="material-icons text-2xl">{{ stat.icon }}</span>
          </div>
          <div class="flex-1">
            <h2 class="text-[1.75rem] font-bold text-text-primary m-0">{{ stat.value }}</h2>
            <p class="text-sm text-text-muted">{{ stat.title }}</p>
          </div>
        </div>
      }
    }
  </div>

  <!-- Chart Row: Zeitverlauf Placeholder + Verteilung nach Kunde -->
  <div class="flex flex-col h-100 xl:flex-row gap-4 mb-4">
    <!-- Entwicklung im Zeitverlauf -->
    <div class="glass-card h-100 flex-1 p-6 rounded-2xl xl:col-span-2">
      <div [ngClass]="!chart().hasData ? 'mb-10' : 'mb-4'" class="flex items-center justify-between gap-3">
        <h2 class="text-xl font-semibold text-text-primary m-0">Entwicklung im Zeitverlauf</h2>
        <p-select
          class="!w-96"
          [options]="timelineMetricOptions"
          [ngModel]="timelineMetric()"
          (ngModelChange)="timelineMetric.set($event)"
          optionLabel="label"
          optionValue="value"
          [filter]="false"
          [showClear]="false"
          styleClass="select-component-style"
          panelStyleClass="select-component-panel-style"
        >
          <ng-template pTemplate="item" let-option>
            <div class="option-row flex items-center gap-2">
              <span class="w-3 h-3 rounded-full shrink-0" [style.background]="option.color"></span>
              <span class="option-label">{{ option.label }}</span>
            </div>
          </ng-template>
        </p-select>
      </div>

      @if (timelineLoading()) {
        <div class="h-[300px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
        </div>
      } @else if (!chart().hasData) {
        <div class="h-[200px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <p class="text-sm text-text-muted">Keine Daten vorhanden</p>
        </div>
      } @else {
        <svg viewBox="0 0 800 300" class="w-full h-[300px]" role="img" aria-label="Entwicklung im Zeitverlauf">
          <defs>
            <linearGradient id="timelineGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" [attr.stop-color]="chart().color" stop-opacity="0.35" />
              <stop offset="100%" [attr.stop-color]="chart().color" stop-opacity="0" />
            </linearGradient>
          </defs>

          <!-- Horizontal gridlines + y-axis labels -->
          @for (line of chart().gridLines; track line.y) {
            <line x1="56" [attr.y1]="line.y" x2="784" [attr.y2]="line.y" stroke="#ffffff" stroke-opacity="0.08" stroke-width="1" />
            <text x="48" [attr.y]="line.y + 4" text-anchor="end" fill="#9fb0c9" font-size="11">{{ line.label }}</text>
          }

          <!-- X-axis labels -->
          @for (lbl of chart().xLabels; track lbl.x) {
            <text [attr.x]="lbl.x" y="294" text-anchor="middle" fill="#9fb0c9" font-size="11">{{ lbl.text }}</text>
          }

          <!-- Area fill + line -->
          <path [attr.d]="chart().areaPath" fill="url(#timelineGradient)" stroke="none" />
          <path
            [attr.d]="chart().linePath"
            fill="none"
            [attr.stroke]="chart().color"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
            vector-effect="non-scaling-stroke"
          />

          <!-- Point markers -->
          @if (chart().showMarkers) {
            @for (p of chart().points; track $index) {
              <circle [attr.cx]="p.x" [attr.cy]="p.y" r="3" [attr.fill]="chart().color" stroke="#0b1220" stroke-width="1.5" />
            }
          }
        </svg>
      }
    </div>

    <!-- Verteilung nach Kunde -->
    <div class="glass-card w-full xl:w-[700px] p-6 rounded-2xl">
      <div class="flex justify-between">
        <h2 class="text-xl font-semibold text-text-primary mb-3">Verteilung nach Kunde</h2>
        <div class="flex items-center justify-between gap-3 w-72 mb-10">
          <p-select
            class="flex-1 min-w-0"
            [options]="donutMetricOptions"
            [ngModel]="donutMetric()"
            (ngModelChange)="donutMetric.set($event)"
            optionLabel="label"
            optionValue="value"
            [filter]="false"
            [showClear]="false"
            styleClass="select-component-style"
            panelStyleClass="select-component-panel-style"
          >
            <ng-template pTemplate="item" let-option>
              <div class="option-row">
                <span class="option-label">{{ option.label }}</span>
              </div>
            </ng-template>
          </p-select>
        </div>
      </div>

      @if (isLoading()) {
        <div class="h-[300px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <div class="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
        </div>
      } @else if (donutSegments().length === 0) {
        <div class="h-[200px] bg-white/5 flex items-center justify-center rounded-[14px] border border-dashed border-white/15">
          <p class="text-sm text-text-muted">Keine Daten vorhanden</p>
        </div>
      } @else {
        <div class="flex items-center gap-4">
          <svg viewBox="0 0 200 200" class="w-40 h-40 shrink-0">
            <g transform="rotate(-90 100 100)">
              @for (seg of donutSegments(); track seg.label) {
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  fill="none"
                  stroke-width="24"
                  [attr.stroke]="seg.color"
                  [attr.stroke-dasharray]="seg.dashArray"
                  [attr.stroke-dashoffset]="seg.dashOffset"
                />
              }
            </g>
            <text x="100" y="96" text-anchor="middle" fill="#e6edf6" font-size="22" font-weight="700">{{ donutTotal() }}</text>
            <text x="100" y="118" text-anchor="middle" fill="#9fb0c9" font-size="11">{{ donutCenterLabel() }}</text>
          </svg>

          <div class="w-full space-y-2.5">
            @for (seg of donutSegments(); track seg.label) {
              <div class="flex items-center gap-2.5">
                <span class="w-3 h-3 rounded-full shrink-0" [style.background]="seg.color"></span>
                <span class="text-sm text-text-secondary truncate w-10 overflow-hidden text-ellipsis flex-1" [title]="seg.label">{{
                  seg.label
                }}</span>
                <span class="text-sm font-semibold text-text-primary whitespace-nowrap">{{ seg.percent }} %</span>
                <span class="text-sm text-text-muted whitespace-nowrap">({{ seg.valueLabel }})</span>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Summary Table -->
  <div class="glass-card p-6 rounded-2xl mb-4">
    <h2 class="text-xl font-semibold text-text-primary mb-4">Zusammenfassung</h2>

    @if (isLoading()) {
      <div class="flex items-center justify-center py-8">
        <div class="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
      </div>
    } @else {
      <div class="overflow-x-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr>
              @for (col of summaryColumns; track col; let first = $first) {
                <th
                  class="px-4 py-3.5 text-sm font-medium text-text-secondary bg-white/6 border-b border-white/10 first:rounded-tl-[10px] last:rounded-tr-[10px] whitespace-nowrap"
                  [class]="first ? 'text-left' : 'text-right'"
                >
                  {{ col }}
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of summaryRows(); track row.label; let last = $last) {
              <tr class="transition-colors hover:bg-white/5" [class.border-b]="!last" [class.border-white/6]="!last">
                <td class="px-4 py-3.5 text-sm font-medium text-text-primary whitespace-nowrap">
                  <span class="inline-flex items-center gap-2">
                    <span class="material-icons text-base text-text-muted">{{ row.icon }}</span>
                    {{ row.label }}
                  </span>
                </td>
                @for (cell of row.cells; track $index) {
                  <td class="px-4 py-3.5 text-sm text-right whitespace-nowrap" [class]="cell === null ? 'text-text-muted' : 'text-text-secondary'">
                    {{ cell === null ? '–' : cell }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>

  <!-- Footer -->
  <p class="text-sm text-text-muted">Zuletzt aktualisiert: {{ lastUpdatedLabel() }}</p>
</div>
